import { router } from 'expo-router';
import { useAtomValue, useSetAtom } from 'jotai';
import * as React from 'react';
import { Separator, YStack } from 'smoothie';

import {
    formatCurrencyInputOnChange,
    formatDollars,
    formatMaskedValue,
    formattedDollarsToFloat,
} from '../../../common/utils/bankingUtils';
import { getCadenceDisplayString } from '../../../common/utils/billUtils';
import { formatAsMonthDayYearUTC } from '../../../common/utils/dateUtils';
import {
    billsAccountsAtom,
    currentlyEditingBillFieldAtom,
} from '../../state/BillsAtoms';
import {
    BillCategoryNames,
    BillContext,
    BillEditTypeParam,
    InlineEditableBillFields,
    type ManualBill,
    type PartialBill,
} from '../../types/Bill';
import BillDetailsRow from '../details/BillDetailsRow';

interface ManualBillAttributesProps {
    bill: ManualBill;
    onUpdateBill: (updatedBill: PartialBill) => void;
    context?: BillContext;
}

const ManualBillAttributes = ({
    bill,
    onUpdateBill,
    context,
}: ManualBillAttributesProps) => {
    const billsAccounts = useAtomValue(billsAccountsAtom);

    const setCurrentlyEditingBillField = useSetAtom(
        currentlyEditingBillFieldAtom,
    );

    const {
        amount,
        payee,
        displayName,
        cadence,
        accountId,
        category,
        lastPaidAt,
    } = bill;

    const account = billsAccounts?.find((a) => a.id === accountId);

    const {
        name: accountName,
        institutionName,
        accountNumberMasked,
    } = account ?? {};

    const updateBill = (key: string, value: string | number | undefined) => {
        onUpdateBill({ ...bill, ...{ [key]: value } });
    };

    const editRoute =
        context === BillContext.REVIEW
            ? '/bills/bill-review-add-new-edit'
            : '/bills/bill-add-new/edit';

    return (
        <YStack separator={<Separator />}>
            <BillDetailsRow
                label={InlineEditableBillFields.AMOUNT}
                value={amount != null ? formatDollars(amount) : ''}
                formattedValue={formatDollars(amount ?? 0)}
                placeholder="Enter amount"
                type="number"
                onPress={() => {
                    setCurrentlyEditingBillField(
                        InlineEditableBillFields.AMOUNT,
                    );
                }}
                onChange={(oldValue, newValue) => {
                    return formatCurrencyInputOnChange(
                        oldValue,
                        newValue === '' ? '' : newValue,
                    );
                }}
                onSubmit={(value) => {
                    updateBill(
                        'amount',
                        value === ''
                            ? undefined
                            : formattedDollarsToFloat(value as string),
                    );
                }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={true}
            />
            <BillDetailsRow
                label={InlineEditableBillFields.PAYEE}
                value={payee ?? ''}
                placeholder="Enter payee"
                onPress={() => {
                    setCurrentlyEditingBillField(
                        InlineEditableBillFields.PAYEE,
                    );
                }}
                onSubmit={(value) => {
                    updateBill('payee', value === '' ? undefined : value);
                }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={true}
            />
            <BillDetailsRow
                label={InlineEditableBillFields.NICKNAME}
                value={displayName ?? ''}
                placeholder="Enter nickname"
                onPress={() => {
                    setCurrentlyEditingBillField(
                        InlineEditableBillFields.NICKNAME,
                    );
                }}
                onSubmit={(value) => {
                    updateBill('displayName', value);
                }}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={true}
            />
            <BillDetailsRow
                label={InlineEditableBillFields.PAID_FROM}
                value={
                    account
                        ? `${accountName}\n${institutionName} ${formatMaskedValue(accountNumberMasked)}`
                        : ''
                }
                placeholder="Select account"
                showCaret={true}
                onPress={() => {
                    router.navigate(
                        `${editRoute}?type=${BillEditTypeParam.ACCOUNT}`,
                    );
                }}
            />
            <BillDetailsRow
                label={InlineEditableBillFields.FREQUENCY}
                value={cadence ?? ''}
                formattedValue={getCadenceDisplayString(cadence)}
                placeholder="Select frequency"
                showCaret={true}
                onPress={() => {
                    router.navigate(
                        `${editRoute}?type=${BillEditTypeParam.FREQUENCY}`,
                    );
                }}
            />
            <BillDetailsRow
                label="Last paid"
                value={
                    lastPaidAt != null
                        ? formatAsMonthDayYearUTC(lastPaidAt)
                        : formatAsMonthDayYearUTC(new Date())
                }
                placeholder="Select date"
                showCaret={true}
                onPress={() => {
                    router.navigate(
                        `${editRoute}?type=${BillEditTypeParam.LAST_PAYMENT_DATE}`,
                    );
                }}
            />
            <BillDetailsRow
                label="Category"
                value={category != null ? BillCategoryNames[category] : ''}
                placeholder="Select category"
                showCaret={true}
                onPress={() => {
                    router.navigate(
                        `${editRoute}?type=${BillEditTypeParam.CATEGORY}`,
                    );
                }}
            />
        </YStack>
    );
};

export default ManualBillAttributes;
